/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
import React, { createContext, useEffect, useState } from 'react';
import Axios from 'axios';
import { PropTypes } from 'prop-types';
import { capitalize } from 'lodash';
import { AssociationUrl, BillsbyUrl } from '../../config/properties';
import { getPlanDetails, getPlanName } from './plans';
import {
  getAssociationValidations,
  putAssociationValidations,
} from './graphql';
import { isFeatureEnabled } from '../../config/featureFlags';

export const SubsContext = createContext();

const getPaymentCard = billsbyCustomerId => {
  const info = billsbyCustomerId.split(' ');
  if (info.length > 1) {
    return {
      cardLast4Digits: info[1],
      cardExpiryMonth: Number(info[2]),
      cardExpiryYear: Number(info[3]),
      cardType: info[4],
    };
  }
  return null;
};

const checkSubscriptionLevel = (auth, iSubscriptionLevel) => {
  if (iSubscriptionLevel === 'None') {
    return 'ICON Core';
  }
  if (auth && auth.role) {
    if (
      auth.role.toUpperCase().includes('GOVERNMENT') ||
      !isFeatureEnabled('ENABLE_BILLSBY')
    ) {
      return iSubscriptionLevel;
    }
    return iSubscriptionLevel || 'New';
  }
  return iSubscriptionLevel;
};

const findIntervals = plans => {
  const dIntervals = ['Monthly', 'Daily', 'Weekly'];
  const intervals = [];
  const plan = plans[0];
  for (let f = 0; f < dIntervals.length; f++) {
    for (let c = 0; c < plan.cycles.length; c++) {
      if (plan.cycles[c].pricingModel.frequencyType === dIntervals[f]) {
        intervals.push(dIntervals[f]);
        break;
      }
    }
  }
  return intervals;
};

const SubscriptionInfo = ({
  auth,
  mySubscriptionLevel = '',
  myBillsbyCustomerId = '',
  myIndustryAssociations = [],
  myOrganizationId = null,
  myPreviousPlan = '',
  children,
}) => {
  const isBillsbyEnabled = isFeatureEnabled('ENABLE_BILLSBY');
  const [plans, setPlans] = useState(null);
  const [intervals, setIntervals] = useState(['Monthly']);
  const [freeTrialPeriod, setFreeTrialPeriod] = useState('');
  const [subscriptionLevel, setSubscriptionLevel] = useState(
    checkSubscriptionLevel(auth, capitalize(mySubscriptionLevel)),
  );
  const [billsbyCustomerId, setBillsbyCustomerId] = useState(
    myBillsbyCustomerId ? myBillsbyCustomerId.split(' ')[0] : '',
  );
  const [industryAssociations, setIndustryAssociations] = useState(
    myIndustryAssociations,
  );
  const [organizationId, setOrganizationId] = useState(myOrganizationId);
  const [associationValidations, setAssociationValidations] = useState([]);
  const [billsbyCard, setBillsbyCard] = useState(
    getPaymentCard(myBillsbyCustomerId),
  );
  const [previousPlan, setPreviousPlan] = useState(myPreviousPlan);
  const [subscription, setSubscription] = useState(null);
  const [flagSubscription, setFlagSubscription] = useState(false);

  const updateSubscription = () => {
    setFlagSubscription(!flagSubscription);
  };

  const setBillsbyCardInfo = (
    customerUniqueId,
    cardLast4Digits,
    expiryMonth,
    expiryYear,
    cardType,
  ) => {
    setBillsbyCard({
      cardType,
      cardLast4Digits,
      cardExpiryMonth: Number(expiryMonth),
      cardExpiryYear: Number(expiryYear),
    });
    return `${customerUniqueId} ${cardLast4Digits} ${expiryMonth} ${expiryYear} ${cardType}`;
  };

  const getBillsbyCardInfo = () => {
    if (billsbyCard) {
      return `${billsbyCustomerId} ${billsbyCard.cardLast4Digits} ${billsbyCard.cardExpiryMonth
        } ${billsbyCard.cardExpiryYear} ${billsbyCard.cardType}`;
    }
    return billsbyCustomerId;
  };

  useEffect(
    () => {
      if (!isBillsbyEnabled) {
        return;
      }

      if (!plans) {
        fetchPlans();
      }

      // Add Billsby script if not already present
      if (!window.billsbyTokens && !document.getElementById('billsby-script')) {
        const script = document.createElement('script');
        script.id = 'billsby-script';
        script.src =
          'https://static.billsby.com/tokenizer/v1/billsby-tokens.js';
        script.async = true;
        script.onload = () => {
          console.log('Billsby script loaded successfully');
          window.billsbyReady = new Promise(resolve => {
            if (window.billsbyTokens) {
              resolve();
            } else {
              const checkBillsby = setInterval(() => {
                if (window.billsbyTokens) {
                  clearInterval(checkBillsby);
                  resolve();
                }
              }, 100);
            }
          });
        };
        script.onerror = () => {
          console.error('Failed to load Billsby script');
        };
        document.body.appendChild(script);
      }
    },
    [isBillsbyEnabled],
  );

  const fetchPlans = async () => {
    try {
      const response = await Axios.get(`${BillsbyUrl}/getPlans`);
      const thePlans = response.data;
      const theIntervals = findIntervals(thePlans);
      setIntervals(theIntervals);
      setFreeTrialPeriod(getPlanDetails('Non-Members', 'ICON Pro', thePlans, theIntervals[0]).freeTrialPeriod);
      setPlans(thePlans)
      return thePlans;
    } catch (error) {
      console.error('Get Plans Error:', error.response);
      return null;
    }
  };

  const fetchSubscription = async (
    customerUniqueId,
    iIndustryAssociations,
    iSubscriptionLevel,
  ) => {
    try {
      let myPlans = plans;
      if (!plans) {
        myPlans = await fetchPlans();
      }
      const response = await Axios.get(
        `${BillsbyUrl}/getSubscription/${customerUniqueId}`,
      );
      const mySubscription = response.data;
      let planName;
      const isFreeTrialEnabled = isFeatureEnabled('ENABLE_FREE_TRIAL');
      if (mySubscription.status === 'InTrial' && isFreeTrialEnabled) {
        if (mySubscription.planIdNext) {
          planName = getPlanName(mySubscription.planIdNext, myPlans);
        } else {
          planName = mySubscription.planName;
        }
        if (planName.startsWith('Members')) {
          planName = 'Members - ICON Pro';
        } else {
          planName = 'Non-Members - ICON Pro';
        }
      } else if (
        mySubscription.status === 'Suspended' ||
        mySubscription.status === 'Cancelled'
      ) {
        if (iIndustryAssociations.length > 0) {
          planName = 'Members - ICON Core';
        } else {
          planName = 'Non-Members - ICON Core';
        }
      } else {
        planName = mySubscription.planName;
      }
      setSubscriptionLevel(
        checkSubscriptionLevel(auth, planName.split(' - ')[1]),
      );
      setSubscription(mySubscription);
    } catch (error) {
      console.log('Billsby getSubscription error:', error.response);
      setSubscriptionLevel(iSubscriptionLevel);
    }
  };

  const fetchAssociationValidations = async () => {
    try {
      const organization = await getAssociationValidations(myOrganizationId);
      console.log('Organization:', organization);
      if (organization) {
        if (
          !matchAffiliations(
            organization.industryAssociations,
            organization.associationValidations,
          )
        ) {
          try {
            const response = await Axios.put(
              `${AssociationUrl}/validate`,
              organization,
            );
            organization.associationValidations = response.data;
            await putAssociationValidations(organization.id, response.data);
          } catch (error) {
            console.log('Association error:', error);
          }
        }
        setAssociationValidations(organization.associationValidations);
      }
    } catch (error) {
      console.log('Error fetching associationValidations:', error);
    }
  };

  useEffect(
    () => {
      if (isBillsbyEnabled && myBillsbyCustomerId) {
        // TODO -- For now, credit card information is stored in billsbyCustomerId: "customerUniqueId last4Digits expiryMonth expiryYear cordType"
        const info = myBillsbyCustomerId.split(' ');
        const customerUniqueId = info[0];
        if (info.length > 1) {
          setBillsbyCardInfo(
            customerUniqueId,
            info[1],
            info[2],
            info[3],
            info[4],
          );
        }
        setBillsbyCustomerId(customerUniqueId);
        setPreviousPlan(myPreviousPlan);
        fetchSubscription(
          customerUniqueId,
          myIndustryAssociations,
          mySubscriptionLevel,
        );
      } else {
        setSubscriptionLevel(
          checkSubscriptionLevel(auth, capitalize(mySubscriptionLevel)),
        );
      }
      setIndustryAssociations(myIndustryAssociations);
    },
    [
      mySubscriptionLevel,
      myBillsbyCustomerId,
      myIndustryAssociations,
      auth,
      isBillsbyEnabled,
    ],
  );

  useEffect(
    () => {
      if (isBillsbyEnabled && billsbyCustomerId) {
        fetchSubscription(
          billsbyCustomerId,
          industryAssociations,
          subscriptionLevel,
        );
      }
    },
    [
      flagSubscription,
      billsbyCustomerId,
      industryAssociations,
      isBillsbyEnabled,
      subscriptionLevel,
    ],
  );

  useEffect(
    () => {
      if (myOrganizationId) {
        setOrganizationId(myOrganizationId);
        fetchAssociationValidations();
      }
    },
    [myOrganizationId, myIndustryAssociations],
  );

  return (
    <SubsContext.Provider
      value={{
        auth,
        plans,
        intervals,
        freeTrialPeriod,
        subscriptionLevel,
        setSubscriptionLevel,
        billsbyCustomerId,
        setBillsbyCustomerId,
        previousPlan,
        setPreviousPlan,
        subscription,
        billsbyCard,
        setBillsbyCard,
        setBillsbyCardInfo,
        getBillsbyCardInfo,
        updateSubscription,
        industryAssociations,
        setIndustryAssociations,
        organizationId,
        setOrganizationId,
        associationValidations,
        setAssociationValidations,
      }}
    >
      {children}
    </SubsContext.Provider>
  );
};

export const matchAffiliations = (
  industryAssociations,
  associationValidations,
) => {
  if (!industryAssociations) {
    industryAssociations = [];
  }
  if (!associationValidations) {
    associationValidations = [];
  }
  if (industryAssociations.length !== associationValidations.length) {
    return false;
  }
  for (let i = 0; i < industryAssociations.length; i++) {
    if (!associationValidations.find(a => a.name === industryAssociations[i])) {
      return false;
    }
  }
  return true;
};

SubscriptionInfo.propTypes = {
  auth: PropTypes.object.isRequired,
  mySubscriptionLevel: PropTypes.string,
  myBillsbyCustomerId: PropTypes.string,
  myIndustryAssociations: PropTypes.array,
  myOrganizationId: PropTypes.string,
  myPreviousPlan: PropTypes.string,
  children: PropTypes.object.isRequired,
};

SubscriptionInfo.defaultProps = {
  mySubscriptionLevel: '',
  myBillsbyCustomerId: '',
  myIndustryAssociations: [],
  myOrganizationId: null,
  myPreviousPlan: '',
};

export default SubscriptionInfo;



// WEBPACK FOOTER //
// ./src/components/Billsby/SubscriptionInfo.jsx